
/*
 * Homecraft Textiles
 * Product Samples inventory control
 *
 * Hide samples when:
 * 1. Inventory tracking is enabled,
 *    Continue selling is disabled,
 *    and inventory is 0 or below.
 *
 * OR
 *
 * 2. The selected variant is "Sample Book".
 *
 * Products without inventory tracking
 * remain eligible for samples.
 */

(() => {
  'use strict';

  const PRODUCT_SELECTOR =
    'product-info[data-sample-stock-check="true"]';

  const SAMPLE_SELECTOR =
    '.product-samples-button-wrap';

  const AVAILABLE_CLASS = 'sample-stock-available';

  let updateScheduled = false;

  function readInventory(product) {
    let element = product.querySelector(
      '[data-sample-inventory]'
    );

    // Inventory data may be located after product-info.
    if (!element) {
      const next = product.nextElementSibling;

      if (next?.matches('script[data-sample-inventory]')) {
        element = next;
      }
    }

    if (!element) {
      console.warn('Sample stock: inventory data missing');
      return null;
    }

    try {
      const raw = element.textContent.replace(
        /([{,]\s*)(\d+)\s*:/g,
        '$1"$2":'
      );

      return JSON.parse(raw);
    } catch (error) {
      console.warn(
        'Sample stock: invalid inventory data',
        error
      );
      return null;
    }
  }

  function readSelectedVariant(product) {
    const element = product.querySelector(
      'variant-picker [data-selected-variant]'
    );

    if (element?.textContent.trim()) {
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.warn(
          'Sample stock: invalid selected variant',
          error
        );
      }
    }

    const input = product.querySelector(
      'form[action*="/cart/add"] input[name="id"]'
    );

    return input?.value ? { id: input.value } : null;
  }

  function updateProduct(product) {
    const inventory = readInventory(product);
    const variant = readSelectedVariant(product);

    const stock =
      inventory && variant?.id != null
        ? inventory[String(variant.id)]
        : null;

    // Check whether the selected fabric is sold out.
    const soldOut = Boolean(
      stock &&
      stock.tracked &&
      stock.policy === 'deny' &&
      Number(stock.quantity) <= 0
    );

    // Check whether the selected option is Sample Book.
    const selectedOptions = [
      ...(Array.isArray(variant?.options)
        ? variant.options
        : []),
      variant?.option1,
      variant?.option2,
      variant?.option3
    ];

    const isSampleBook = selectedOptions.some(
      (option) =>
        typeof option === 'string' &&
        option.trim().toLowerCase() === 'sample book'
    ) || (
      typeof variant?.title === 'string' &&
      variant.title.trim().toLowerCase() === 'sample book'
    );

    // Hide when sold out OR when Sample Book is selected.
    const hideSample = soldOut || isSampleBook;

    product.classList.toggle(
      AVAILABLE_CLASS,
      !hideSample
    );

    // Update all sample buttons created by the app.
    product
      .querySelectorAll(SAMPLE_SELECTOR)
      .forEach((wrapper) => {
        wrapper.hidden = hideSample;

        if (hideSample) {
          wrapper.style.setProperty(
            'display',
            'none',
            'important'
          );
        } else {
          wrapper.style.removeProperty('display');
        }

        wrapper.querySelectorAll('button').forEach(
          (button) => {
            button.disabled = hideSample;
          }
        );
      });
  }

  function updateAll() {
    document
      .querySelectorAll(PRODUCT_SELECTOR)
      .forEach(updateProduct);
  }

  function scheduleUpdate() {
    if (updateScheduled) return;

    updateScheduled = true;

    requestAnimationFrame(() => {
      updateScheduled = false;
      updateAll();
    });
  }

  // Listen for changes to the selected variant.
  document.addEventListener(
    'variant:changed',
    scheduleUpdate
  );

  document.addEventListener('change', (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest('variant-picker')
    ) {
      scheduleUpdate();
    }
  });

  // Handle Shopify theme editor updates.
  document.addEventListener(
    'shopify:section:load',
    scheduleUpdate
  );

  function initialize() {
    updateAll();

    // Handle buttons added dynamically by Product Samples.
    const observer = new MutationObserver((changes) => {
      const domChanged = changes.some(
        (change) =>
          change.addedNodes.length > 0 ||
          change.removedNodes.length > 0
      );

      if (domChanged) {
        scheduleUpdate();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
